import { Link, NavLink, useLocation, Outlet, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Buildings,
  ChartBar,
  ClipboardText,
  Gauge,
  SignOut,
  Users,
} from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { useAuth } from "../../contexts/AuthContext";
import { isSuperAdminRole } from "../../utils/roles";
import { cn } from "@/lib/utils";

/**
 * AdminLayout - Clean admin chrome with top bar and secondary nav
 */
export default function AdminLayout() {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await signOut();
    navigate("/admin/login");
  };

  const navItems = [
    { label: "Dashboard", path: "/admin", icon: Gauge, testId: "admin-nav-dashboard", end: true },
    { label: "Usage and Cost", path: "/admin/usage-and-cost", icon: ChartBar, testId: "admin-nav-usage-cost" },
    { label: "Users", path: "/admin/users", icon: Users, testId: "admin-nav-users" },
    ...(isSuperAdminRole(user?.role)
      ? [{ label: "Organizations", path: "/admin/organizations", icon: Buildings, testId: "admin-nav-organizations" }]
      : []),
    { label: "Audit Log", path: "/admin/audit", icon: ClipboardText, testId: "admin-nav-audit" },
  ];

  return (
    <div className="flex min-w-0 flex-1 flex-col overflow-y-auto bg-slate-50" data-testid="admin-layout">
      <header className="flex-shrink-0 border-b border-slate-200 bg-white px-4 pt-5 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1
              className="font-cabinet text-xl font-black tracking-tight text-slate-900"
              data-testid="admin-portal-title"
            >
              Admin Portal
            </h1>
            <p className="mt-0.5 text-sm text-slate-500" data-testid="admin-portal-scope-label">
              {isSuperAdminRole(user?.role)
                ? "Manage platform access, organizations, usage, and system activity."
                : `Manage ${user?.organization_name || "your organization"}, its users, usage, and activity.`}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button asChild variant="outline" size="sm" className="rounded-sm text-xs font-semibold">
              <Link to="/" data-testid="admin-back-to-app">
                <ArrowLeft size={16} />
                Back to App
              </Link>
            </Button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              className="rounded-sm text-xs font-semibold"
              data-testid="admin-logout-button"
            >
              <SignOut size={16} />
              Logout
            </Button>
          </div>
        </div>

        <div className="-mx-4 overflow-x-auto px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
          <nav className="mt-4 flex min-w-max items-center gap-1" aria-label="Admin sections">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = item.end
                ? location.pathname === item.path
                : location.pathname.startsWith(item.path);
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.end}
                  data-testid={item.testId}
                  aria-current={isActive ? "page" : undefined}
                  className={cn(
                    "flex items-center gap-1.5 whitespace-nowrap rounded-t-md border-b-2 px-4 py-2 text-sm font-medium transition-colors",
                    isActive
                      ? "border-primary bg-primary/5 text-primary"
                      : "border-transparent text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                  )}
                >
                  <Icon size={14} weight={isActive ? "bold" : "regular"} />
                  {item.label}
                </NavLink>
              );
            })}
          </nav>
        </div>
      </header>

      <div className="w-full min-w-0 max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <Outlet />
      </div>
    </div>
  );
}
